import axios from 'axios'
import LRU from 'lru-cache'
import { ERROR } from '~/constants/common'
import { IResponseError } from '~/types/axios'

const cache = new LRU({
  max: 500,
  ttl: 1000 * 30 // 30s
})

export default defineNuxtPlugin((nuxtApp) => {
  const { $ua, ssrContext } = nuxtApp as Record<string, any>
  const runtimeConfig = useRuntimeConfig()
  const requestHeaders = useRequestHeaders()
  const headers: Record<string, string> = {}

  if ($ua) {
    headers.os = $ua.os()
    headers.device = $ua.deviceType()
    headers.browser = $ua.browser()
  }

  const axiosInstance = axios.create({
    headers,
    withCredentials: true,
    baseURL: process.server ? runtimeConfig.API_BASE_URL : window.location.origin
  })

  axiosInstance.interceptors.request.use((config) => {
    if (ssrContext) {
      const cookies = (config.headers?.cookie?.split(';') ?? []).map((cookie: string) => cookie.trim())
      cookies?.push?.(`brand_code=${ssrContext?.runtimeConfig?.BRAND_CODE}`)
      cookies?.push?.(`host=${requestHeaders.host}`)
      cookies?.push?.('renderMode=ssr')

      cookies?.push?.(`device=${$ua.isFromPc() ? 'desktop' : 'mobile'}`)
      if (config.headers) {
        config.headers.cookie = cookies.join('; ')
      }
    }
    if (config.url?.startsWith('/api-promotion/')) {
      config.baseURL = runtimeConfig.API_PROMOTION_BASE_URL
    }

    const cacheKey = `${config.url}/${$ua.deviceType()}`
    if (ssrContext && config.cacheSsr) {
      const cachedData = cache.get(cacheKey)
      if (cachedData) {
        config.data = undefined // Will be ignored by axios
        config.headers.fromCache = true
        return config
      }
    }

    return config
  })

  axiosInstance.interceptors.response.use(
    function (response) {
      const cacheKey = `${response.config.url}/${$ua.deviceType()}`

      if (response.config.headers && response.config.headers.fromCache) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) {
          response.data = cachedData
        }

        return response
      }

      if (ssrContext && response.config.headers && !response.config.headers.fromCache) {
        const dataToCache = {
          data: response.data.data,
          status: response.data.status,
          code: response.data.code
        }
        cache.set(cacheKey, dataToCache)
      }

      return response
    },
    function (error: IResponseError) {
      if (!ssrContext) {
        const nuxtApp = useNuxtApp()
        const code = parseInt(error?.response?.status ?? 0)
        const requestUrl = error?.config?.url || ''
        const allowShow401MsgApis = ['/login', '/register', '/logout']
        const isBlockShow401Msg = code === 401 && allowShow401MsgApis.some((api) => requestUrl.includes(api))
        if (!error?.config.bypassErrorToast && (isBlockShow401Msg || code !== 401)) {
          nuxtApp.$alertMsg('error', error?.response?.data?.message || ERROR)
        }
      }
      throw error
    }
  )

  nuxtApp.provide('axios', axiosInstance)
})
